import type { RatingHistoryEntry } from "./homeApi";

export interface RatingChartPoint {
  index: number;
  playedAt: string;
  playedAtTs: number;
  rating: number;
  tournamentNumber: number | null;
  matchCount: number;
}

/**
 * One point per tournament, not per match. The ledger stores a snapshot per match
 * and every match of a tournament carries the same date, so plotting them raw
 * stacks a vertical column of dots on a single X position. Each point keeps the
 * rating the player walked out of that tournament with.
 *
 * Entries are collapsed in run order (the API returns them in ELO compute order),
 * so a tournament revisited later stays a separate point instead of folding back.
 */
export function buildRatingChartPoints(
  history: RatingHistoryEntry[],
  tournamentNumberAt: (index: number) => number | null,
): RatingChartPoint[] {
  const points: RatingChartPoint[] = [];

  history.forEach((entry, index) => {
    const tournamentNumber = entry.tournamentNumber ?? tournamentNumberAt(index);
    const previous = points[points.length - 1];
    const sameTournament =
      previous &&
      (tournamentNumber === null
        ? previous.tournamentNumber === null && previous.playedAt === entry.playedAt
        : previous.tournamentNumber === tournamentNumber);

    if (sameTournament) {
      previous.rating = entry.ratingAfter;
      previous.playedAt = entry.playedAt;
      previous.playedAtTs = new Date(entry.playedAt).getTime();
      previous.matchCount += 1;

      return;
    }

    points.push({
      index: points.length + 1,
      playedAt: entry.playedAt,
      playedAtTs: new Date(entry.playedAt).getTime(),
      rating: entry.ratingAfter,
      tournamentNumber,
      matchCount: 1,
    });
  });

  return points;
}
